import { apiClient, ApiResponse } from './client';
import { Competitor } from '@/types/competitor';

// Define API endpoints
export const API_ENDPOINTS = {
  saveCompetitor: '/api/v4/competitor',
  fetchCompetitors: '/api/v4/competitors',
  generic: '/generic',
  pricing: '/api/v4/pricing',
  pricePredictionJob: '/price_prediction_job',
  pricePredictionAnalysis: '/price_prediction_job/analysis',
  createRoomTypeCompetitor: '/price_prediction_job/createRoomTypeCompetitor',
  getCompetitorRooms: '/price_prediction_job/get_competitor_rooms',
  competitorAnalysisData: '/revenue_manager_hotel_room_types/getCompetitorAnalysisData',
};

export interface GenericPatchPayload {
  resource_name: string;
  column_value_pair: Array<{
    id: string | number;
    data: Record<string, any>;
  }>;
}

export interface GenericPostPayload {
  resource_name: string;
  column_value_pair: Array<Record<string, any>>;
}

export interface GenericDeletePayload {
  resource_name: string;
  column_value_pair: {
    ids: (string | number)[];
  };
}

/**
 * Interface for price prediction analysis response
 */
export interface PricePredictionAnalysisResponse {
  success: boolean;
  message: string;
  data: {
    job_id: string;
    analysis_data: any; // Analysis data structure
    daily_data?: any[]; // Daily pricing analysis data
    property_mappings?: Record<string, string[]>; // Room mappings data
  };
}

/**
 * Execute a generic POST request to save data
 */
export async function genericPost(
  payload: GenericPostPayload[]
): Promise<ApiResponse<any>> {
  const response = await apiClient.post(API_ENDPOINTS.generic, payload);
  return response.data;
}

/**
 * Execute a generic GET request to fetch data
 */
export async function genericGet(
  resourceName: string,
  filter?: Record<string, any>
): Promise<ApiResponse<any>> {
  const params = filter ? { filter: JSON.stringify(filter) } : {};
  const response = await apiClient.get(`${API_ENDPOINTS.generic}/${resourceName}`, { params });
  return response.data;
}

/**
 * Save a competitor using the generic endpoint
 */
export async function saveCompetitor(
  competitor: Competitor,
  branchId: number
): Promise<ApiResponse<any>> {
  const payload: GenericPostPayload[] = [
    {
      resource_name: 'Competitor',
      column_value_pair: [
        {
          branch_id: branchId,
          name: competitor.name,
          trackedOTAs: JSON.stringify(competitor.trackedOTAs),
          ...(competitor.id && { id: competitor.id }),
          ...(competitor.location && { location: competitor.location }),
          ...(competitor.distance !== undefined && { distance: competitor.distance }),
          ...(competitor.distanceUnit && { distanceUnit: competitor.distanceUnit }),
          ...(competitor.starRating !== undefined && { starRating: competitor.starRating }),
          ...(competitor.category && { category: competitor.category }),
          ...(competitor.rooms !== undefined && { rooms: competitor.rooms }),
          ...(competitor.amenities && competitor.amenities.length > 0 && { amenities: JSON.stringify(competitor.amenities) }),
          is_active: competitor.is_active,
        },
      ],
    },
  ];

  const response = await genericPost(payload);
  
  // Handle nested response structure: response[0].data.Competitor_data[0]
  if (Array.isArray(response) && response.length > 0) {
    const firstResponse = response[0];
    if (firstResponse.success && firstResponse.data?.Competitor_data?.[0]) {
      return {
        success: true,
        data: firstResponse.data.Competitor_data[0].data,
        message: firstResponse.data.Competitor_data[0].message,
      };
    }
  }
  
  // Fallback to original response if structure is different
  return response;
}

/**
 * Delete a competitor using the generic endpoint
 */
export async function deleteCompetitor(
  competitorId: string
): Promise<ApiResponse<any>> {
  const payload: GenericDeletePayload[] = [
    {
      resource_name: 'Competitor',
      column_value_pair: {
        ids: [competitorId],
      },
    },
  ];

  return genericDelete(payload);
}

/**
 * Execute a generic DELETE request
 */
export async function genericDelete(
  payload: GenericDeletePayload[]
): Promise<ApiResponse<any>> {
  const response = await apiClient.delete(API_ENDPOINTS.generic, { data: payload });
  
  // Handle nested response structure: response.data[0].data[0]
  if (Array.isArray(response.data) && response.data.length > 0) {
    const resourceResponse = response.data[0];
    if (resourceResponse.data && Array.isArray(resourceResponse.data) && resourceResponse.data.length > 0) {
      const deleteResult = resourceResponse.data[0];
      return {
        success: deleteResult.success,
        message: deleteResult.message,
        data: deleteResult,
      };
    }
  }
  
  // Fallback to original response if structure is different
  return response.data;
}

/**
 * Execute a generic PATCH request
 */
export async function genericPatch(
  payload: GenericPatchPayload[]
): Promise<ApiResponse<any>> {
  const response = await apiClient.patch(API_ENDPOINTS.generic, payload);
  
  // Handle the nested response structure
  if (Array.isArray(response.data) && response.data.length > 0) {
    const resourceResponse = response.data[0];
    
    if (resourceResponse.data && Array.isArray(resourceResponse.data) && resourceResponse.data.length > 0) {
      const recordUpdate = resourceResponse.data[0];
      
      // Return a normalized response that matches the expected format
      return {
        success: recordUpdate.success,
        message: recordUpdate.message,
        data: recordUpdate.data
      };
    }
  }
  
  // Return original response if structure is different than expected
  return response.data;
}

/**
 * Execute a pricing or availability rule
 * Updates execution timestamps in the database
 */
export async function executeRule(
  ruleId: string | number,
  resourceName: 'PricingRule' | 'AvailabilityRule' = 'PricingRule'
): Promise<ApiResponse<any>> {
  // Get current timestamp in UTC without timezone issues
  const now = new Date();
  const currentTimestamp = new Date(Date.UTC(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    now.getHours(),
    now.getMinutes(),
    now.getSeconds(),
    now.getMilliseconds()
  )).toISOString();
  
  const payload: GenericPatchPayload[] = [
    {
      resource_name: resourceName,
      column_value_pair: [
        {
          id: ruleId,
          data: {
            last_executed_at: null,
            execution_start_date: currentTimestamp,
          },
        },
      ],
    },
  ];

  return genericPatch(payload);
}

/**
 * Update a competitor using the generic endpoint
 * @param competitorId - The ID of the competitor to update
 * @param updateData - Object containing fields to update
 * @returns Promise with the API response
 */
export async function updateCompetitor(
  competitorId: string | number,
  updates: {
    name?: string;
    location?: string;
    distance?: number;
    distanceUnit?: 'km' | 'mi';
    starRating?: number;
    category?: string;
    rooms?: number;
    amenities?: string[];
    trackedOTAs?: string[];
    is_active?: boolean;
  }
): Promise<ApiResponse<any>> {
  // Transform updateData keys to API format
  const transformedData: Record<string, any> = {};
  
  // Map each property from updateData to the API format
  if (updates.name !== undefined) transformedData.name = updates.name;
  if (updates.location !== undefined) transformedData.location = updates.location;
  if (updates.distance !== undefined) transformedData.distance = updates.distance;
  if (updates.distanceUnit !== undefined) transformedData.distanceUnit = updates.distanceUnit;
  if (updates.starRating !== undefined) transformedData.starRating = updates.starRating;
  if (updates.category !== undefined) transformedData.category = updates.category;
  if (updates.rooms !== undefined) transformedData.rooms = updates.rooms;
  if (updates.amenities !== undefined && updates.amenities.length > 0) {
    transformedData.amenities = JSON.stringify(updates.amenities);
  }
  
  if (updates.trackedOTAs !== undefined && updates.trackedOTAs.length > 0) {
    transformedData.trackedOTAs = JSON.stringify(updates.trackedOTAs);
  }
  
  if (updates.is_active !== undefined) {
    transformedData.is_active = updates.is_active;
  }
  
  // Create the payload for the PATCH request
  const payload: GenericPatchPayload[] = [
    {
      resource_name: 'Competitor',
      column_value_pair: [
        {
          id: competitorId,
          data: transformedData,
        },
      ],
    },
  ];

  // Use generic PATCH method for updates
  return genericPatch(payload);
}

/**
 * Fetch competitors for a specific branch using the generic API endpoint
 */
export async function fetchCompetitors(branchId: number): Promise<Competitor[]> {
  try {
    // Create the filters parameter as expected by the API
    const filters = [
      {
        resource_name: 'Competitor',
        columns: ["id",
          "branch_id",
          "name",
          "location",
          "distance",
          "distanceUnit",
          "starRating",
          "category",
          "rooms",
          "amenities",
          "trackedOTAs",
          "is_active",
          "createdAt"],
        column_filters: [{ branch_id: branchId }]
      }
    ];
    // Make the API call using the correct format
    const response = await apiClient.get(`${API_ENDPOINTS.generic}`, { 
      params: { filters: JSON.stringify(filters) }
    });
    
    // Handle the response structure as shown in the example
    if (response.data && 
        response.data.success && 
        Array.isArray(response.data.data) && 
        response.data.data.length > 0 && 
        response.data.data[0].Competitor_data) {
      
      const competitors = response.data.data[0].Competitor_data;
      console.log('Fetched competitors from API:', competitors);
      
      // Transform API response to Competitor objects
      return competitors.map((item: any) => ({
        id: item.id ? String(item.id) : `comp-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        name: item.name,
        location: item.location || '',
        distance: item.distance ? Number(item.distance) : undefined,
        distanceUnit: item.distanceUnit,
        starRating: item.starRating ? Number(item.starRating) : undefined,
        category: item.category || 'mid-range',
        rooms: item.rooms ? Number(item.rooms) : undefined,
        amenities: item.amenities ? JSON.parse(item.amenities) : [],
        trackedOTAs: item.trackedOTAs ? JSON.parse(item.trackedOTAs) : [],
        is_active: Boolean(item.is_active),
        createdAt: new Date(item.createdAt || Date.now()),
        updatedAt: new Date(item.updatedAt || Date.now()),
        branch_id: item.branch_id
      }));
    }
    return [];
  } catch (error) {
    console.error('Error fetching competitors:', error);
    return [];
  }
}

/**
 * Fetch price prediction jobs for a specific branch
 * @param branchId The branch ID to fetch jobs for
 * @returns Promise with the price prediction job data
 */
export interface PricePredictionJob {
  id: number;
  org_id: number;
  branch_id: number;
  job_id: string;
  status: string;
  start_date: string;
  no_of_analytical_days: number;
  property_ota_json?: string;
  property_mapping_json?: string;
  createdAt: string;
  updatedAt: string;
}

export async function fetchPricePredictionJobs(branchId: number): Promise<PricePredictionJob[]> {
  try {
    // Create the filters parameter as expected by the API
    const filters = [
      {
        resource_name: 'PricePredictionJob',
        columns: [
          'id',
          'org_id',
          'branch_id',
          'job_id',
          'status',
          'start_date',
          'no_of_analytical_days',
          'property_ota_json',
          'property_mapping_json',
          'createdAt',
          'updatedAt'
        ],
        column_filters: [{ branch_id: branchId }]
      }
    ];
    
    // Make the API call using the correct format
    const response = await apiClient.get(`${API_ENDPOINTS.generic}`, { 
      params: { filters: JSON.stringify(filters) }
    });
    
    // Handle the response structure
    if (response.data && 
        response.data.success && 
        Array.isArray(response.data.data) && 
        response.data.data.length > 0 && 
        response.data.data[0].PricePredictionJob_data) {
      
      return response.data.data[0].PricePredictionJob_data;
    }

    return [];
  } catch (error) {
    console.error('Error fetching price prediction jobs:', error);
    return [];
  }
}

/**
 * Update a price prediction job's property_ota_json and start_date
 * @param jobId The job ID to update
 * @param propertyOtaJson The new property OTA JSON data
 * @param startDate Optional start date for the job
 */
export async function updatePricePredictionJob(
  jobId: string,
  propertyOtaJson: string,
  startDate?: string,
  endDate?: string,
  numberOfDays?: number
): Promise<ApiResponse<any>> {
  const updateData: Record<string, any> = {
    property_ota_json: propertyOtaJson
  };
  
  if (startDate) {
    updateData.start_date = startDate;
  }
  if (endDate) {
    updateData.end_date = endDate;
  }
  if (numberOfDays) {
    updateData.no_of_analytical_days = numberOfDays;
  }
  
  // Use direct patch endpoint for price prediction job
  const response = await apiClient.patch(`${API_ENDPOINTS.pricePredictionJob}/${jobId}`, updateData);
  return response.data;
}

/**
 * Fetch competitor room mappings for a specific branch using the generic API endpoint
 */
/**
 * Fetch analysis data for a specific price prediction job
 * @param jobId The ID of the price prediction job to fetch analysis for
 */
export async function fetchPricePredictionAnalysis(
  jobId: string
): Promise<ApiResponse<any>> {
  try {
    const response = await apiClient.get(`${API_ENDPOINTS.pricePredictionAnalysis}/${jobId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching price prediction analysis:', error);
    return {
      success: false,
      message: 'Failed to fetch analysis data',
      data: null
    };
  }
}

export async function fetchCompetitorRoomMappings(branchId: number) {
  try {
    // Create the filters parameter as expected by the API
    const filters = [
      {
        resource_name: 'CompetitorRoomMapping',
        columns: ['branch_id', 'competitor_hotel_name', 'branch_name', 'mappings'],
        column_filters: [{ branch_id: branchId }]
      }
    ];
    
    // Make the API call using the format shown in the provided URL
    const response = await apiClient.get(`${API_ENDPOINTS.generic}`, { 
      params: { filters: JSON.stringify(filters) }
    });
    
    // Handle the response structure as shown in the example
    if (response.data && 
        response.data.success && 
        Array.isArray(response.data.data) && 
        response.data.data.length > 0 && 
        response.data.data[0].CompetitorRoomMapping_data) {
      
      const mappingsData = response.data.data[0].CompetitorRoomMapping_data;
      console.log('Fetched room mappings from API:', mappingsData);
      
      return mappingsData.map((item: any) => ({
        branchId: item.branch_id,
        competitorHotelName: item.competitor_hotel_name,
        branchName: item.branch_name,
        mappings: item.mappings ? JSON.parse(item.mappings) : []
      }));
    }
    return [];
  } catch (error) {
    console.error('Error fetching competitor room mappings:', error);
    return [];
  }
}

/**
 * Trigger room type competitor creation for active competitors
 * Called when competitors are added or updated
 * @param activeCompetitorNames - Array of active competitor property names
 */
export async function triggerRoomTypeCompetitor(
  activeCompetitorNames: string[]
): Promise<ApiResponse<any>> {
  try {
    const payload = activeCompetitorNames.map(name => ({
      property_name: name
    }));
    
    console.log('Triggering createRoomTypeCompetitor with payload:', payload);
    
    const response = await apiClient.post(API_ENDPOINTS.createRoomTypeCompetitor, payload);
    return response.data;
  } catch (error) {
    console.error('Error triggering room type competitor:', error);
    return {
      success: false,
      message: 'Failed to trigger room type competitor creation',
      data: null
    };
  }
}

/**
 * Fetch competitor rooms on demand by competitor name
 * @param competitorName - The name of the competitor property (will be URL encoded)
 */
export async function fetchCompetitorRoomsOnDemand(
  competitorName: string
): Promise<ApiResponse<any>> {
  try {
    const encodedName = encodeURIComponent(competitorName);
    console.log('Fetching competitor rooms for:', competitorName);
    
    const response = await apiClient.get(`${API_ENDPOINTS.getCompetitorRooms}/${encodedName}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching competitor rooms:', error);
    return {
      success: false,
      message: 'Failed to fetch competitor rooms',
      data: null
    };
  }
}

/**
 * Fetch competitor analysis data (room types) for room mapping dialog
 * @param branchId - The branch ID
 * @param startDate - Start date in YYYY-MM-DD format (defaults to today)
 * @param endDate - End date in YYYY-MM-DD format (defaults to 14 days from start)
 */
export async function fetchCompetitorAnalysisData(
  branchId: number,
  startDate?: string,
  endDate?: string
): Promise<ApiResponse<any>> {
  try {
    const today = new Date();
    const start = startDate || today.toISOString().split('T')[0];
    const end = endDate || new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    
    const filters = {
      start_date: start,
      end_date: end,
      is_for_cm: true,
      branch_id: branchId
    };
    
    const queryString = `filters=${encodeURIComponent(JSON.stringify(filters))}`;
    console.log('Fetching competitor analysis data with filters:', filters);
    
    const response = await apiClient.get(`${API_ENDPOINTS.competitorAnalysisData}?${queryString}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching competitor analysis data:', error);
    return {
      success: false,
      message: 'Failed to fetch competitor analysis data',
      data: null
    };
  }
}
